<script setup lang="ts">
import type { ITableFilterProps } from '@modules/tables/components/VTableFilter/types';
import type { ITableColumnsOptions } from '@modules/tables/types';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VMerchantLogoPreview from '@modules/merchant-settings/components/VMerchantLogoPreview/VMerchantLogoPreview.vue';
import { useMerchantLogoPreview } from '@modules/merchant-settings/composables';
import { MERCHANT_SETTINGS_PERMISSIONS, TRANSLATION_GROUP_MERCHANT_SETTINGS } from '@modules/merchant-settings/constants';
import { useMerchantSettingsListQuery } from '@modules/merchant-settings/queries';
import { ADMIN_MERCHANT_SETTINGS_EDIT_ROUTE_NAME } from '@modules/merchant-settings/submodules/edit/constants';
import { ESmartFilterColumnType } from '@modules/smart-filters/enums';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useMerchantSettingsListQuery);

const {
  merchantLogoUrl,
  merchantName,
  onPreview,
  onPreviewHide,
} = useMerchantLogoPreview();

const tableFilterOptions: ITableFilterProps = {
  pathForFilter: 'api2/admin/merchants',
  actionForFilter: 'list',
  filterName: 'merchant',
};

const columnOptions: ITableColumnsOptions = {
  merchant_name: {
    defaultMobilePreview: true,
    type: ESmartFilterColumnType.CHECKBOX,
  },
  id: {
    type: ESmartFilterColumnType.COMPARE,
  },
  merchant_id: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
};
</script>

<template>
  <VCard v-data-qa-auto-id="'MerchantSettingsPage'" type="table">
    <VTable
      :data="data"
      :title-t-path="$getTPath(TRANSLATION_GROUP_MERCHANT_SETTINGS, 'HEADER')"
      :loading="isLoading"
      :is-fetching="isFetching"
      :state="tableState"
      :filter-props="tableFilterOptions"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="listItem.logo_url"
          icon="pi-eye"
          @click="onPreview(listItem.logo_url, listItem.merchant_name)"
        />
        <VIconButton
          v-if="$hasPermission(MERCHANT_SETTINGS_PERMISSIONS.EDIT)"
          icon="pi-file-edit"
          :to="{ name: ADMIN_MERCHANT_SETTINGS_EDIT_ROUTE_NAME, params: { id: listItem.id } }"
        />
      </template>
    </VTable>
  </VCard>

  <VMerchantLogoPreview
    :logo-url="merchantLogoUrl"
    :title="merchantName"
    @hide="onPreviewHide"
  />
</template>
